import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, User, Users, Trash2, Edit } from 'lucide-react';
import { Doctor, Staff, Room, ScheduleItem } from './SchedulerTypes';

interface DoctorQuickReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleItem: ScheduleItem | null;
  doctor: Doctor | null;
  staff: Staff | null;
  room: Room | null;
  onDeleteSchedule?: (scheduleId: string) => void;
  onEditSchedule?: (scheduleItem: ScheduleItem) => void;
  allSchedulesForDay?: ScheduleItem[];
  allStaff?: Staff[];
  allRooms?: Room[];
}

export default function DoctorQuickReportModal({
  isOpen,
  onClose,
  scheduleItem,
  doctor,
  staff,
  room,
  onDeleteSchedule,
  onEditSchedule,
  allSchedulesForDay = [],
  allStaff = [],
  allRooms = []
}: DoctorQuickReportModalProps) {
  if (!scheduleItem || !doctor) return null;

  // Helper functions to get staff and room by ID
  const getStaffById = (staffId: string) => {
    return allStaff.find(s => s.id === staffId);
  };

  const getRoomById = (roomId: string) => {
    return allRooms.find(r => r.id === roomId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getScheduleTypeColor = (type: string) => {
    switch (type) {
      case 'appointment':
        return 'bg-blue-100 text-blue-800';
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'break':
        return 'bg-orange-100 text-orange-800';
      case 'leave':
        return 'bg-red-500 text-white';
      case 'mc':
        return 'bg-red-700 text-white';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const schedulesToShow = allSchedulesForDay.length > 0 ? allSchedulesForDay : [scheduleItem];
  const hasMultipleSchedules = schedulesToShow.length > 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Doctor Daily Report
            {hasMultipleSchedules && (
              <Badge variant="secondary" className="ml-2">
                {schedulesToShow.length} schedules
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Doctor Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: doctor.color }}
                />
                {doctor.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{doctor.specialization}</p>
            </CardHeader>
          </Card>

          {/* Schedule Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{formatDate(scheduleItem.date)}</span>
            </div>

            {schedulesToShow.map((schedule, index) => (
              <Card key={schedule.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Schedule {hasMultipleSchedules ? `${index + 1}` : ''}</span>
                    <Badge className={getScheduleTypeColor(schedule.type)}>
                      {schedule.type === 'mc' ? 'Urgent Leave' : schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1)}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{schedule.startTime} - {schedule.endTime}</span>
                  </div>

                  <div className="text-sm">
                    <strong>Title:</strong> {schedule.title}
                  </div>

                  {schedule.notes && (
                    <div className="text-sm">
                      <strong>Notes:</strong> {schedule.notes}
                    </div>
                  )}

                  {schedule.roomId && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Room: {getRoomById(schedule.roomId)?.name || schedule.roomId}
                        {getRoomById(schedule.roomId)?.clinic && ` - ${getRoomById(schedule.roomId)?.clinic}`}
                      </span>
                    </div>
                  )}

                  {schedule.staffId && (
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Collaborative Staff:</span>
                        {(() => {
                          const staffMember = getStaffById(schedule.staffId);
                          return staffMember ? (
                            <div className="flex items-center gap-2 mt-1">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: staffMember.color }}
                              />
                              <span className="text-sm">{staffMember.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {staffMember.role}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Staff ID: {schedule.staffId}</span>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-3">
                    {onEditSchedule && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          onEditSchedule(schedule);
                          onClose();
                        }}
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                    )}
                    {onDeleteSchedule && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          // Extract base UUID from composite IDs (removes _day_X suffix)
                          const baseId = schedule.id.split('_day_')[0];
                          onDeleteSchedule(baseId);
                        }}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 